import { backendBadgeLabel } from '../../../_helpers/backend-badge-label';
import { getActiveSubscriptionPlanId } from '../../../_helpers/subscription-access';
import { SUBSCRIPTION_PLAN_LABELS } from '../../../_helpers/subscription-plan';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { NgClass, NgFor, CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, Observable, of, catchError, switchMap, map, startWith, shareReplay } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InrShortPipe } from '../../../_pipes/indian-currency.pipe';
import { NumberFormatPipe } from '../../../_pipes/number-format.pipe';
import { PropertyService } from '../../../_services/properties/property.service';
import { Router, RouterModule } from '@angular/router';
import { SharingdataService } from '../../../_services/sharingdata.service';
import { LoaderService } from '../../../_services/loader.service';
import { ToastService } from '../../../_services/toast.service';
import { ViewChildren, QueryList } from '@angular/core';
import { AuthService } from '../../../_services/auth.service';
import { isPropertyRestricted, propertyFeatureSession } from '../../../_helpers/property-feature-access';
import { CiReport, CiMetric } from '../view-area/ci-report';
import { rentalYieldIndicator } from '../view-area/ci-indicators';
import { CiReportService } from '../../../_services/properties/ci-report.service';

@Component({
  selector: 'app-search-aside',
  standalone: true,
  imports: [
    NgFor,
    NgClass,
    CommonModule,
    InrShortPipe,
    NumberFormatPipe,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './search-aside.component.html',
  styleUrl: './search-aside.component.scss',
})
export class SearchAsideComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('propertyContainer') propertyContainer!: ElementRef;
  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef;
  @ViewChildren('scrollAnchor') scrollAnchors!: QueryList<ElementRef>;


  private observer!: IntersectionObserver;
  private destroy$ = new Subject<void>();

  @Input() propertyList: any[] = [];
  @Output() selectedItemChange = new EventEmitter<any>();
  @Input() hasActiveFilters = false;
  @Input() filteredCount = 0;
  @Input() activeTab = 'all-listings';
  @Output() tabChanged = new EventEmitter<string>();

  private _selectedItem: any = null;
  private hasUserClicked = false;

  @Input()
  set selectedItem(value: any) {
    if (this.isMobile && !this.hasUserClicked) {
      this._selectedItem = null;
      return;
    }
    this._selectedItem = value;
  }
  get selectedItem() {
    return this._selectedItem;
  }

  isMobile = false;
  readonly backendBadgeLabel = backendBadgeLabel;

  currentPage = 1;
  limit = 10;
  isLoading = false;
  hasMore = true;
  totalProperties = 0;
  favProperties: any = [];
  favCount: number = 0;
  receivedData:any
  constructor(
    public _PS: PropertyService,
    private router: Router,
    private _SD: SharingdataService,
    private _NS: ToastService,
    private ciReports: CiReportService,
    public auth: AuthService
  ) {
    effect(() => {
      this.receivedData =this._PS.data();
      console.log('Data changed:', this.receivedData);
    });
  }

  ngOnInit() {
    this.checkScreen();
    this.auth.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (propertyFeatureSession(user) === propertyFeatureSession(this.membershipUser)) return;
      this.membershipUser = user;
      this._PS.viewStatus.set(null);

      this._PS.getViewPropertyStatus().pipe(takeUntil(this.destroy$)).subscribe({ error: () => {} });
    });
    this._SD.currentResults$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.currentPage = 1;
        if (Array.isArray(res) && res.length === 0) {
          // filters cleared → back to paginated "all properties" mode
          this.hasMore = true;
          this.propertyList = res ?? [];
        } else {
          // a search/filter is active → results are already complete, so
          // infinite-scroll must not page through the unfiltered list
          this.hasMore = false;
        }
        setTimeout(() => this.reAttachObserver(), 0);
      });
    this.loadFavs();
    this._PS.favList$.subscribe((list) => {
      this.loadFavs();
    });
  }

  ngAfterViewInit() {
    this.scrollToSelected();
    this.setupIntersectionObserver();
    this.viewedProperties()
  }
  reAttachObserver() {
    if (this.observer) {
      this.observer.disconnect(); // disconnect old one first
    }
    this.setupIntersectionObserver(); // attach fresh observer
  }

  setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') return;
    const anchor = this.scrollAnchors.first;
    if (!anchor?.nativeElement) return;

    const options = { root: null, threshold: 0.5 };
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.isLoading && this.hasMore) {
        this.loadMore();
      }
    }, options);

    this.observer.observe(anchor.nativeElement);
  }

  loadMore() {
    // Search/filtered results are fetched in full by the search API — never
    // page through the unfiltered "all properties" endpoint while a search
    // is active, or unrelated properties get merged into the search results.
    if (this.isLoading || !this.hasMore || this.hasActiveFilters) return;

    this.isLoading = true;
    if (this.receivedData && Object.keys(this.receivedData).length > 0) {
      this.currentPage = this.receivedData.page ?? this.currentPage;
    }
  
    this._PS
      .getAllProperties(this.currentPage, this.limit)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: any) => {
          this.totalProperties = res.meta.total;
          this._PS.setData(null)
          const currentMap = new Map(
            this.propertyList.map((item) => [item._id, item])
          );
          res.data.forEach((item: any) => currentMap.set(item._id, item));
          this.propertyList = Array.from(currentMap.values());

          this.hasMore = this.propertyList.length < res.meta.total;
          if (this.hasMore) this.currentPage++;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  @HostListener('window:resize')
  checkScreen() {
    if (typeof window === 'undefined') return;
    this.isMobile = window.innerWidth < 768;
  }
  private membershipUser: any = null;
  userType:any ='Basic'
  get accessLabel(): string {
    const plan = getActiveSubscriptionPlanId(this.membershipUser);
    if (plan !== 'basic') return SUBSCRIPTION_PLAN_LABELS[plan] + ' access';
    const status = this._PS.viewStatus();
    if (!status) return 'Basic · 3 free property views';
    const remaining = Math.max(0, Number(status.viewLimit ?? 3) - Number(status.viewedCount ?? 0));
    return `Basic · ${remaining} free property ${remaining === 1 ? 'view' : 'views'} left`;
  }
  get showUpgrade(): boolean { return getActiveSubscriptionPlanId(this.membershipUser) === 'basic'; }

  isRestricted(property: any): boolean {
    return isPropertyRestricted(this.membershipUser, this._PS.viewStatus(), property?._id);
  }

  onPropertySelect(property: any, index: any, detailTab: 'property' | 'intelligence' = 'property'): void {
    if (this.isRestricted(property)) {
      this.router.navigate(['/payment']);
      return;
    }
    // if(['Premium'].includes(this.userType)){
      this.savedViewProperty(property._id)
    // }

    this.router.navigate(['/search'], {
      queryParams: {
        name: property.name,
        id: property._id,
        tab: detailTab,
      },
      queryParamsHandling: 'merge',
    });
    this.hasUserClicked = true;
    this._selectedItem = { ...property, selectedPro: index + 1, detailTab };
    this.selectedItemChange.emit(this._selectedItem);
    setTimeout(() => this.scrollToSelected(), 0);
  }
  onTabChange(tabId: string) {
    this.activeTab = tabId;
    this.tabChanged.emit(tabId);
    setTimeout(() => this.reAttachObserver(), 0);
  }
  onFilterClick(): void {}

  private ciPreviews = new Map<string | object, Observable<CiReport | null>>();
  private ciPreviewStates = new Map<string | object, Observable<{ status: 'loading' | 'available' | 'unavailable' | 'error'; report: CiReport | null }>>();

  ciPreviewState(property: any): Observable<{ status: 'loading' | 'available' | 'unavailable' | 'error'; report: CiReport | null }> {
    const key = property._id || property;
    let state = this.ciPreviewStates.get(key);
    if (!state) {
      state = this.auth.user$.pipe(switchMap(() => {
        return this.ciReports.getPreview(property).pipe(
          map(report => ({ status: report ? 'available' as const : 'unavailable' as const, report })),
          startWith({ status: 'loading' as const, report: null }),
          catchError(() => of({ status: 'error' as const, report: null })),
        );
      }), shareReplay({ bufferSize: 1, refCount: true }));
      this.ciPreviewStates.set(key, state);
    }
    return state;
  }

  ciPreview(property: any): Observable<CiReport | null> {
    const key = property._id || property;
    let preview = this.ciPreviews.get(key);
    if (!preview) {
      preview = this.ciPreviewState(property).pipe(map(state => state.report));
      this.ciPreviews.set(key, preview);
    }
    return preview;
  }

  ciTeaser(report: CiReport): string {
    if (report.propertyType === 'Plots') return 'Land Assessment · Land Adjustments';
    if (report.propertyType === 'Independent House') return 'Land & Building Assessment · Offer Guidance';
    if (report.propertyType === 'Primary') return 'Comparable Projects · UDS Analysis · Rental Analysis';
    return 'Transaction Data · UDS Analysis · Rental Analysis';
  }

  ciMetric(metrics: CiMetric[], label: string): string {
    return metrics.find(metric => metric.label === label)?.value || 'Not available';
  }

  rentalYieldRating(report: CiReport): string {
    return rentalYieldIndicator(report);
  }

  negotiationPotentialRating(report: CiReport, property: any): string {
    return report.negotiationRating ?? 'Not available';
  }

  get filteredProperties() {
    if (this.activeTab === 'favorites') {
      return this.favProperties;
    }

    if (this.activeTab === 'recently-viewed') {
      return []; // plug real data later
    }

    return this.propertyList;
  }

  private scrollToSelected(): void {
    if (!this.propertyContainer) return;
    const container = this.propertyContainer.nativeElement;
    const selectedElement = container?.querySelector('[data-selected="true"]');
    if (selectedElement) {
      selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
  ngOnDestroy() {
    if (this.observer) this.observer.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- rest of your methods unchanged ---
  getPriceRange(prices: any[]): { min: string; max: string; isSame: boolean } | null {
    if (!Array.isArray(prices)) return null;
    const values = prices.filter(p => p?.price != null && p.price !== '').map(p => Number(p.price)).filter(Number.isFinite).sort((a, b) => a - b);
    if (!values.length) return null;
    return {
      min: values[0].toString(),
      max: values[values.length - 1].toString(),
      isSame: values[0] === values[values.length - 1],
    };
  }

  formatAddress(property: any): string {
    const cleanCluster = property?.microMarket?.cluster?.replace(
      /\s*\(.*?\)/g,
      ''
    ); // removes (C5), (C4), etc.

    const parts = [property?.microMarket?.micromarketName, cleanCluster];

    return parts.filter(Boolean).join(', ');
  }

  get getTabData() {
    return [
      {
        id: 'all-listings',
        label: 'All listings',
        count: this.propertyList.length,
      },
      {
        id: 'favorites',
        label: 'Favorites',
        count: this.favProperties.length ?? 0,
      },
      { id: 'recently-viewed', label: 'Recently viewed', count: this.viewPropertyDetails?.total ? this.viewPropertyDetails?.total : 0 },
    ];
  }

  isExpertModalOpen = false;
  isFormSubmitted = false;
  expertForm = { name: '', email: '', mobile: '', message: '' };

  openExpertModal() {
    this.isExpertModalOpen = true;
    this.isFormSubmitted = false;
  }
  closeExpertModal() {
    this.isExpertModalOpen = false;
  }
  submitExpertForm() {
    this.isFormSubmitted = true;
  }

  getUniqueBhkTypes = (properties: any) => {
    const formattedTypes = (Array.isArray(properties) ? properties : []).map((item: any) =>
    item?.bhkType ? item.bhkType.replace(/\s+/g, '').toUpperCase():""
    );
    return [...new Set(formattedTypes)];
  };

  getPropertySummaries = (properties: any) => {
    const structureTypes = properties.map((i: any) => i.propertyStructureType);
    return [...new Set(structureTypes)];
  };
  // In any property card component
  errorMsg: any;
  isFavLoading = false;
  loadFavs(): void {
    // this.isLoading = true;
    this.isFavLoading = true;
    this.errorMsg = '';
    this._PS.getFavs().subscribe({
      next: (res) => {
        this.favProperties = res.data ?? [];
        this.favCount = res.totalFavourites;
        // this.isLoading = false;
        this.isFavLoading = false;
      },
      error: (err) => {
        this.errorMsg = err.message;
        // this.isLoading = false;
        this.isFavLoading = false;
        this._NS.error(err.message);
      },
    });
  }
  togglingFavId: string | null = null;

  toggleFav(event: Event, propertyId: string): void {
    event.stopPropagation();
    const isFav = this._PS.isFav(propertyId);
    this.togglingFavId = propertyId;
    const action$ = isFav
      ? this._PS.removeFav(propertyId)
      : this._PS.addFav(propertyId);
    action$.subscribe({
      next: (res) => {
        this.togglingFavId = null;
        this._NS.success(
          isFav ? 'Removed from favourites' : 'Added to favourites'
        );
        if (isFav) {
          this.favProperties = this.favProperties.filter(
            (p: any) => p._id !== propertyId
          );
          if (this.selectedItem?._id === propertyId) {
            this.selectedItem = this.favProperties[0] ?? null;
          }
        } else {
          if (res?.data) {
            this.favProperties = [...this.favProperties, res.data];
          }
        }
      },
      error: (err) => {
        this.togglingFavId = null;
        this._NS.error(err.message);
      },
    });
  }
  savedViewProperty(propertyId:string){
    this._PS.getViewedProp(propertyId).subscribe({
      next: (res: any) => {
        this._PS.getViewPropertyStatus().pipe(takeUntil(this.destroy$)).subscribe({ error: () => {} });
      },
      error: (err: any) => {
        
      },
    });
  }
  viewPropertyDetails: any = { data: [], total: 0 };
  viewedProperties() {
    this._PS.getViewedProperies().subscribe({
      next: (res: any) => {
        if (res.status) {
          this.viewPropertyDetails = {
            ...res,
            data: [...res.data].reverse()
          };
        }
      },
      error: (err: any) => {},
    });
  }
  isViewed(propertyId: string): boolean {
    return this.viewPropertyDetails?.data?.some((v: any) => v._id === propertyId) ?? false;
  }
}
